/* tslint:disable:align */
//============================================================================
// Name        : ReceiveItemLot.tsx
// Purpose     : Implement Warehouse receive UI
// Created     : 2019-06-26
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

// tslint:disable-next-line: no-unused-expression
/** @jsx jsx */ jsx;
import {css, jsx} from "@emotion/core";
import moment from "moment";
import React, {RefObject, SyntheticEvent} from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { ValueType } from "react-select/src/types";
import * as GlobalSettings from "../../globalSettings";
import { IRemoveLotAction, IUpdateLotAction } from "./actions/receiveActions";
import DatePicker from "./DatePicker";
import { ChangeInputEvent, IReceiveItemLotProps, ReceiveActions } from "./types";

// tslint:disable-next-line: interface-name
interface OptionType { value: string | number; label: string; }

const inputPendingStyle = css`
  outline: 3px #d53530 solid !important;
  box-shadow: none !important;
  outline-offset: -2px;
`;

const expMissingStyle = css`
    display: flex;
    margin-top: 10px;
`;

interface IReceiveItemLotState {
    receiveQty: boolean;
    storageUnitSelected: boolean;
    lotNumberModified: boolean;
    expDateModified: boolean;
    palletCount: boolean;
    palletCases: boolean;
    individualCases: boolean;
    customStorageUnit: boolean;
}

class ReceiveItemLot extends React.Component<IReceiveItemLotProps, IReceiveItemLotState> {
    lotNumberField: RefObject<DatePicker>;
    expDateField: RefObject<DatePicker>;
    storageUnitChanged: boolean;

    constructor(props: IReceiveItemLotProps) {
        super(props);
        if (!this.props.defaultStorageUnit) {
            alert("Unable to get default storage unit");
        }
        else {
            this.props.updateLot(this.props.itemId, this.props.lot.id, {
                storageUnit: {
                    value: this.props.defaultStorageUnit.id,
                    label: this.props.defaultStorageUnit.name
                }
            });
        }

        this.lotNumberField = React.createRef();
        this.expDateField = React.createRef();
        this.state = {
            receiveQty: false,
            storageUnitSelected: false,
            lotNumberModified: false,
            expDateModified: false,
            palletCount: false,
            palletCases: false,
            individualCases: false,
            customStorageUnit: false
        };
    }

    onChange = (event: ChangeInputEvent) => {
        let newState = {};
        if (event.target.name == "receiveQty" ||
            event.target.name == "palletCount" ||
            event.target.name == "palletCases" ||
            event.target.name == "individualCases" ||
            event.target.name == "customStorageUnit") {

            // Don't allow decimals
            if (event.target.name != "customStorageUnit") {
                newState = { [event.target.name]: Math.round(parseInt(event.target.value, 10)).toString() };
            }
            else {
                newState = { [event.target.name]: event.target.value };
            }

            switch (event.target.name) {
                case "receiveQty":
                    this.setState({ receiveQty: true });
                    break;
                case "palletCount":
                    this.setState({ palletCount: true });
                    break;
                case "palletCases":
                    this.setState({ palletCases: true });
                    break;
                case "individualCases":
                    this.setState({ individualCases: true });
                    break;
                case "customStorageUnit":
                    this.setState({ customStorageUnit: true });
                    break;
            }
        }
        else if (event.target.name == "missingExpDate") {
            newState = { [event.target.name]: event.target.checked };
        }
        else {
            newState = { [event.target.name]: event.target.value };
        }
        this.props.updateLot(this.props.itemId, this.props.lot.id, newState);
    }

    onLotNumberChanged = (value: string) => {
        const newState = {  lotNumber: value };

        // Show confirm date change dialog
        this.props.showChangeDateConfirmation(
            () => {
                console.log(newState);
                this.props.updateLot(this.props.itemId, this.props.lot.id, newState);
                this.setState({ lotNumberModified: true });
                this.props.hideCancelConfirmation();
            },
            () => {
                this.lotNumberField.current.undoValueUpdate();
                this.props.hideCancelConfirmation();
            }
        );
    }

    onExpirationDateChanged = (value: string) => {
        const newState = {  expirationDate: value };
        const newDate = moment(value, "YYYY-MM-DD");
        const curDate = moment();

        if (newDate < curDate) {
            this.props.showChangeDateConfirmation(
                () => {
                    this.props.updateLot(this.props.itemId, this.props.lot.id, newState);
                    this.setState({ expDateModified: true });
                    this.props.hideCancelConfirmation();
                },
                () => {
                    this.expDateField.current.undoValueUpdate();
                    this.props.hideCancelConfirmation();
                }
            );
        }
        else {
            this.props.updateLot(this.props.itemId, this.props.lot.id, newState);
            if (!value.includes("-0")) {
                this.setState({ expDateModified: true });
            }
            else {
                this.setState({ expDateModified: false });
            }
        }
    }

    onBlur = (event: SyntheticEvent) => {
        this.props.updateLot(this.props.itemId, this.props.lot.id, {});

        const el = event.target as HTMLInputElement;

        if (parseInt(el.value, 10) > 0) {
            return;
        }

        switch (el.name) {
            case "palletCount":
                this.setState({ palletCount: true });
                break;
            case "palletCases":
                this.setState({ palletCases: true });
                break;
            case "individualCases":
                this.setState({ individualCases: true });
                break;
            case "customStorageUnit":
                this.setState({ customStorageUnit: true });
        }
        this.props.updateLot(this.props.itemId, this.props.lot.id, {});
    }

    onFocus = (event) => {
        event.target.select();
    }

    onStorageUnitChange = (storageUnit) => {
        this.props.updateLot(this.props.itemId, this.props.lot.id, { storageUnit });
    }

    onToggleCustomStorageUnit = () => {
        const newState = { hasCustomStorageUnit: !this.props.lot.hasCustomStorageUnit };
        this.props.updateLot(this.props.itemId, this.props.lot.id, newState);
    }

    onRemoveRow = () => {
        this.props.removeLot(this.props.itemId, this.props.lot.id);

        setTimeout(() => {
            this.getItem().lots.map(l => this.props.updateLot(this.props.itemId, l.id, {}));
        }, 500);
    }

    getItem = () => this.props.reception.external_receive_items.find(it => it.id == this.props.itemId);

    shouldShowCustomStorageOption = (): boolean => {
        const lineUom = this.getItem().line_uofm_code;
        return lineUom == "LB" || lineUom == "LBS" || lineUom == "EA" || lineUom == "EA1";
    }

    storageUnitMissing = () => this.props.lot.storageUnitMissing(this.getItem().defaultStorageUnit);

    render() {
        const maxDate = moment(new Date()).format("YYYY-MM-DD");
        const expCalc = this.getItem().catalogItem.mf_exp_calculation;

        // noinspection HtmlUnknownTarget
        const shouldShowCustomStorageOption = this.shouldShowCustomStorageOption();
        return (
            <tr>
                <td>
                    <input css={!this.state.receiveQty && !this.props.lot.isValid && inputPendingStyle} className="mf-input-sm" type="number"
                           min={0} step={1} autoFocus={true} name="receiveQty"
                           value={this.props.lot.receiveQty} onChange={this.onChange}
                           onBlur={this.onBlur} onFocus={this.onFocus} />
                </td>
                <td>
                    <div css={[this.storageUnitMissing() && inputPendingStyle, { width: "90px", padding: "5px" }]}>
                        <Select styles={GlobalSettings.selectCustomStyle}
                            isSearchable={false}
                            options={this.props.storageUnits}
                            value={this.props.lot.storageUnit as ValueType<OptionType>}
                            onChange={this.onStorageUnitChange}
                            blurInputOnSelect={true}
                            onBlur={this.onBlur} />
                    </div>
                </td>
                <td>
                    <div css={[!this.state.lotNumberModified && !this.props.lot.isValid && inputPendingStyle]}>
                        <DatePicker ref={this.lotNumberField} value={this.props.lot.lotNumber} maxDate={maxDate} onDateChanged={this.onLotNumberChanged} onBlur={this.onBlur} />
                    </div>
                </td>
                <td>
                    <div css={[!this.state.expDateModified && !this.props.lot.isValid && inputPendingStyle,
                        (expCalc == "" && { marginTop: "33px" }),
                        (expCalc != "" && { marginTop: "52px" }) ]}>
                        <DatePicker ref={this.expDateField} value={this.props.lot.expirationDate} minIsCurrent={true} onDateChanged={this.onExpirationDateChanged} onBlur={this.onBlur} />
                    </div>
                    <div className="checkbox" css={{ justifyContent: "center" }}>
                        <label css={expMissingStyle}>
                            <input type="checkbox" name="missingExpDate" checked={this.props.lot.missingExpDate} onChange={this.onChange} />
                            <span className="cr"><i className="cr-icon fa fa-check"></i></span>
                            <span css={{ fontSize: "14px" }}>Missing Expiration Data</span>
                        </label>
                    </div>
                    {expCalc != "" && <div style={{marginLeft: "20px"}}>Expiration Data: <b>{expCalc}</b></div>}

                </td>

                <td>
                    <input css={!this.state.palletCount && !this.props.lot.isValid && inputPendingStyle} className="mf-input-sm" type="number"
                           min={0} step={1} name="palletCount" value={this.props.lot.palletCount}
                           onChange={this.onChange} onBlur={this.onBlur} onFocus={this.onFocus} />
                </td>
                <td>
                    <input css={!this.state.palletCases && !this.props.lot.isValid && inputPendingStyle} className="mf-input-sm" type="number"
                           min={0} step={1} name="palletCases" value={this.props.lot.palletCases}
                           onChange={this.onChange} onBlur={this.onBlur} onFocus={this.onFocus} />
                </td>
                <td>
                    <input css={!this.state.individualCases && !this.props.lot.isValid && inputPendingStyle} className="mf-input-sm" type="number"
                           min={0} step={1} name="individualCases" value={this.props.lot.individualCases}
                           onChange={this.onChange} onBlur={this.onBlur} onFocus={this.onFocus} />
                </td>

                <td>
                    {!this.props.lot.hasCustomStorageUnit && this.props.defaultStorageUnit.measure_unit}
                    {this.props.lot.hasCustomStorageUnit && <input type="number" value={this.props.lot.customStorageUnit} className="mf-input-sm"
                        css={[!this.state.customStorageUnit && !this.props.lot.isValid && inputPendingStyle, { width: 90 }]} min={0}
                        name="customStorageUnit" onChange={this.onChange} onBlur={this.onBlur} onFocus={this.onFocus} />}
                </td>

                <td css={{ minWidth: "115px" }}>
                    {this.props.lot.confirmLotValid(this.getItem().defaultStorageUnit) && <img className="mf-valid-img" alt="Valid" src="/images/greencheck.png" />}
                    {!this.props.lot.confirmLotValid(this.getItem().defaultStorageUnit) && <img alt="Invalid" css={{ height: "38px" }} src="/images/cancel.png" />}
                </td>
                <td>
                    <div css={{ display: "flex", alignItems: "center" }}>
                        {shouldShowCustomStorageOption && <a onClick={this.onToggleCustomStorageUnit} css={{ textDecoration: "none" }} href="#">Toggle Custom UOM</a>}
                        {this.props.lot.id > 0 && <a css={{ marginLeft: 14 }} href="#" onClick={this.onRemoveRow}>Delete</a>}
                    </div>
                </td>
            </tr>
        );
    }
}

const mapStateToProps = state => ({
    reception:            state.receiveOrders.reception,
    showConfirmation:     state.receiveOrders.showConfirmation,
    confirmationTitle:    state.receiveOrders.confirmationTitle,
    confirmationMsg:      state.receiveOrders.confirmationMsg
});
const mapDispatchToProps = dispatch => ({
    updateLot: (itemId: number, lotId: number, newState: object, storageUnitSelected: boolean) => dispatch({
        type: ReceiveActions.UpdateLot,
        payload: { itemId, lotId, newState, storageUnitSelected }
    } as IUpdateLotAction),
    removeLot: (itemId: number, lotId: number) => dispatch({
        type: ReceiveActions.RemoveLot,
        payload: { itemId, lotId }
    } as IRemoveLotAction),
    hideCancelConfirmation: () => dispatch({
        type: ReceiveActions.HideCancelConfirmation
    }),
    showChangeDateConfirmation: (positiveCb, negativeCb) => dispatch({
        type: ReceiveActions.ShowChangeDateConfirmation,
        payload: { positiveCb, negativeCb }
    })
});

export default connect(mapStateToProps, mapDispatchToProps)(ReceiveItemLot);
